import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PasswordInput from "@/components/PasswordInput";

const PENGELOLA = [
  { key: "kelembagaan", label: "KELEMBAGAAN & KERJA SAMA" },
  { key: "akademik", label: "AKADEMIK & RISBANG" },
  { key: "kemahasiswaan", label: "KEMAHASISWAAN" },
  { key: "sdpt", label: "SUMBER DAYA PERGURUAN TINGGI" },
  { key: "sistem-informasi", label: "SISTEM INFORMASI" },
  { key: "hukum-kepegawaian", label: "HUKUM, KEPEGAWAIAN & TATA LAKSANA" },
  { key: "keuangan", label: "KEUANGAN DAN RENGGAR" },
  { key: "tata-usaha", label: "TATA USAHA" },
  { key: "ppid", label: "PPID" },
  { key: "spi", label: "SPI" },
  { key: "rbzi", label: "RB & ZI" },
  { key: "pts", label: "PERGURUAN TINGGI SWASTA" },
];

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [pengelola, setPengelola] = useState("");
  const [pin, setPin] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [pinHint, setPinHint] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [methods, setMethods] = useState(null);
  const [method, setMethod] = useState("pin");

  useEffect(() => {
    setMethods(null);
    setMethod("pin");
    if (!pengelola) return;
    let alive = true;
    (async () => {
      try {
        const data = await api(`/api/auth/methods/${pengelola}`, {}, false);
        if (!alive) return;
        setMethods(data);
        setMethod(
          pengelola === "ppid" || data.has_credentials ? "cred" : "pin"
        );
      } catch {
        if (alive) setMethods({ has_pin: true, has_credentials: false });
      }
    })();
    return () => {
      alive = false;
    };
  }, [pengelola]);

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F3F7FB] text-sm font-semibold text-slate-500">
        Memuat...
      </div>
    );
  }
  if (user) {
    return <Navigate to={user.role === "ppid" ? "/admin" : "/dashboard"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!pengelola) {
      setError("Pilih akun pengelola terlebih dahulu.");
      return;
    }
    const payload =
      method === "cred"
        ? { pengelola, username: username.trim(), password }
        : { pengelola, pin };
    if (method === "cred") {
      if (!username.trim() || !password) {
        setError("Masukkan username dan password Anda.");
        return;
      }
    } else if (!pin) {
      setError("Masukkan PIN Anda.");
      return;
    }
    setLoading(true);
    try {
      const data = await api("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setUser(data);
      toast(`Selamat datang, ${data.label}!`);
      navigate(data.role === "ppid" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const isAdmin = pengelola === "ppid";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F3F7FB] px-6 py-12">
      <div className="w-full max-w-md rounded-3xl border border-[#E3EBF3] bg-white p-8 shadow-[0_18px_44px_rgba(11,36,64,0.10)] md:p-10">
        <div className="flex flex-col items-center text-center">
          <img
            src="/logo.webp"
            alt="Logo Tut Wuri Handayani — Kemdiktisaintek"
            data-testid="login-logo"
            className="h-14 w-14 object-contain"
          />
          <h1
            data-testid="login-title"
            className="mt-4 font-display text-3xl font-semibold tracking-tight text-[#0B2440] md:text-4xl"
          >
            APIK
          </h1>
          <p className="mt-2 text-sm font-semibold text-[#1479BF]">
            Administrasi Pelaporan Informasi Publik
          </p>
          <p className="mt-1 text-sm text-slate-500">
            PPID — LLDIKTI Wilayah VII
          </p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label
              htmlFor="pengelola"
              className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-[#0B2440]"
            >
              Pengelola
            </label>
            <select
              id="pengelola"
              data-testid="login-pengelola-select"
              value={pengelola}
              onChange={(e) => {
                setPengelola(e.target.value);
                setPin("");
                setUsername("");
                setPassword("");
                setError("");
              }}
              className="w-full rounded-xl border border-[#E3EBF3] bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-[#1479BF]"
            >
              <option value="">Pilih Akun</option>
              {PENGELOLA.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          {methods?.has_pin && methods?.has_credentials && (
            <div
              data-testid="login-method-toggle"
              className="flex flex-wrap gap-2"
            >
              <button
                type="button"
                data-testid="login-method-pin"
                onClick={() => {
                  setMethod("pin");
                  setError("");
                }}
                className={`rounded-full px-4 py-2 text-[11px] font-bold tracking-[0.08em] transition-colors duration-300 ${
                  method === "pin"
                    ? "bg-[#1479BF] text-white"
                    : "border border-[#E3EBF3] bg-white text-slate-600 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
                }`}
              >
                LOGIN DENGAN PIN
              </button>
              <button
                type="button"
                data-testid="login-method-cred"
                onClick={() => {
                  setMethod("cred");
                  setError("");
                }}
                className={`rounded-full px-4 py-2 text-[11px] font-bold tracking-[0.08em] transition-colors duration-300 ${
                  method === "cred"
                    ? "bg-[#1479BF] text-white"
                    : "border border-[#E3EBF3] bg-white text-slate-600 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
                }`}
              >
                LOGIN DENGAN USERNAME
              </button>
            </div>
          )}

          {methods !== null && method === "cred" ? (
            <>
              <div>
                <label
                  htmlFor="username"
                  className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-[#0B2440]"
                >
                  Username
                </label>
                <input
                  id="username"
                  autoComplete="username"
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setError("");
                  }}
                  placeholder="Masukkan username"
                  className="w-full rounded-xl border border-[#E3EBF3] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#1479BF]"
                />
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-[#0B2440]"
                >
                  Password
                </label>
                <PasswordInput
                  id="password"
                  autoComplete="current-password"
                  testid="login-password-input"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  placeholder="Masukkan password"
                  className="w-full rounded-xl border border-[#E3EBF3] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#1479BF]"
                />
              </div>
            </>
          ) : (
            <div>
              <label
                htmlFor="pin"
                className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-[#0B2440]"
              >
                PIN
              </label>
              <PasswordInput
                id="pin"
                inputMode="numeric"
                autoComplete="off"
                testid="login-pin-input"
                value={pin}
                onChange={(e) => {
                  setPin(e.target.value);
                  setError("");
                }}
                onFocus={() => setPinHint(true)}
                placeholder="Masukkan PIN"
                className="w-full rounded-xl border border-[#E3EBF3] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#1479BF]"
              />
              {pinHint && (
                <p
                  data-testid="login-pin-hint"
                  className="mt-2 text-xs font-medium text-[#E8A33D]"
                >
                  Isi PIN yang diberikan oleh PPID
                </p>
              )}
            </div>
          )}

          {error && (
            <p
              data-testid="login-error"
              className="rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-600"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full rounded-full bg-[#1479BF] px-6 py-3.5 text-xs font-bold tracking-[0.08em] text-white transition-colors duration-300 hover:bg-[#0B2440] disabled:opacity-60"
          >
            {loading ? "MEMPROSES..." : "MASUK"}
          </button>
        </form>

        {isAdmin && (
          <div data-testid="login-google-section" className="mt-8 border-t border-[#E3EBF3] pt-6">
            <p className="text-center text-xs font-semibold text-slate-500">
              Atau masuk sebagai Admin dengan akun Google
            </p>
            <button
              data-testid="login-google-button"
              onClick={googleLogin}
              className="mt-4 flex w-full items-center justify-center gap-2.5 rounded-full border border-[#E3EBF3] bg-white px-5 py-3 text-xs font-semibold text-slate-600 transition-colors duration-300 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4">
                <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.1 2.8-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z" />
                <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1C3.3 21.3 7.3 24 12 24z" />
                <path fill="#FBBC05" d="M5.3 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3V6.6H1.3C.5 8.2 0 10 0 12s.5 3.8 1.3 5.4l4-3.1z" />
                <path fill="#EA4335" d="M12 4.7c1.8 0 3.3.6 4.6 1.8l3.4-3.4C18 1.2 15.2 0 12 0 7.3 0 3.3 2.7 1.3 6.6l4 3.1c.9-2.9 3.6-5 6.7-5z" />
              </svg>
              Masuk dengan Google
            </button>
          </div>
        )}

        <p className="mt-8 text-center text-[11px] text-slate-400">
          Halaman ini khusus pengelola data. Masyarakat tidak perlu masuk untuk
          mengakses informasi publik.
        </p>
      </div>
    </div>
  );
}
